import { useContext, useEffect, useState, type RefObject } from "react";

import { AppContext } from "@/AppContext";

// RV-1 — shared bits for the Reviews screens: light/dark (the app's own setting), relative times,
// and the review type names.

export type Theme = "light" | "dark";

// Reviews follows the app's own light / dark setting, and its switch flips that setting, so the
// room matches the rest of Launch OS. Share and My reviews pages (no sign-in, e.g. from a phone's
// home screen) aren't wired to the app's setting, so the choice is also remembered here.
export function useReviewTheme(): [Theme, () => void] {
  const { darkMode, setDarkMode } = useContext(AppContext);
  const [mine, setMine] = useState<Theme | null>(() => { try { const t = localStorage.getItem("lr-theme"); return t === "dark" || t === "light" ? t : null; } catch (e) { return null; } });
  const theme: Theme = mine || (darkMode ? "dark" : "light");
  const toggle = () => {
    const next: Theme = theme === "dark" ? "light" : "dark";
    setMine(next);
    try { localStorage.setItem("lr-theme", next); } catch (e) {}
    // AppContext types the setter loosely; it is the app's useState setter (the avatar menu calls it the same way)
    (setDarkMode as unknown as (on: boolean) => void)?.(next === "dark");
  };
  // the page behind the Reviews screen takes the theme's color: on an iPhone home-screen app the
  // screen can stop above the home-bar area, and that strip would otherwise show white
  useEffect(() => {
    const bg = theme === "dark" ? "#1c1c1c" : "#f9fafb", els = [document.documentElement, document.body];
    const was = els.map((el) => el.style.backgroundColor);
    els.forEach((el) => { el.style.backgroundColor = bg; });
    let meta = document.head.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement("meta"); meta.setAttribute("name", "theme-color"); document.head.appendChild(meta); }
    meta.setAttribute("content", bg);
    return () => els.forEach((el, i) => { el.style.backgroundColor = was[i]; });
  }, [theme]);
  return [theme, toggle];
}

export function ago(iso?: string | null): string {
  if (!iso) return "";
  const s = (Date.now() - new Date(iso).getTime()) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)} d ago`;
  return new Date(iso).toLocaleDateString([], { month: "short", day: "numeric" });
}

export const TYPE_LABEL: Record<string, string> = { short: "Short form", long: "Long form", page: "Page", decision: "Decision", questions: "Questions", media: "Media review" };

export const ThemeIcon = ({ theme }: { theme: Theme }) =>
  theme === "dark" ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z" /></svg>
  );

export const MicIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="9" y="3" width="6" height="11" rx="3" /><path d="M5 11a7 7 0 0 0 14 0" /><path d="M12 18v3" /></svg>
);

export const NoteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" /></svg>
);

export const SideIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M9 4v16" /></svg>
);

// full screen for a room (the button in its top bar, or F; Esc leaves). iPhones don't allow it in
// the browser; there, adding Reviews to the home screen gives the same full screen.
export function useFullscreen(target: RefObject<HTMLElement>, toast: (t: string) => void): [boolean, () => void] {
  const [on, setOn] = useState(false);
  useEffect(() => {
    const sync = () => setOn(!!document.fullscreenElement && document.fullscreenElement === target.current);
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, [target]);
  const toggle = () => {
    if (document.fullscreenElement) { document.exitFullscreen().catch(() => {}); return; }
    const el = target.current;
    if (!el?.requestFullscreen) { toast("Full screen isn't available in this browser. On a phone, add Reviews to the home screen."); return; }
    el.requestFullscreen().catch(() => toast("Full screen isn't available here."));
  };
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if ((e.key === "f" || e.key === "F") && !e.metaKey && !e.ctrlKey && !e.altKey && !t?.closest?.("input,textarea,select,[contenteditable]") && !document.querySelector(".lr-ovl")) { e.preventDefault(); toggle(); }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  });
  return [on, toggle];
}

export const FullIcon = ({ on }: { on: boolean }) =>
  on ? (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></svg>
  ) : (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
  );

export const AreaIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 8V4h4M16 4h4v4M20 16v4h-4M8 20H4v-4" /><circle cx="12" cy="12" r="2" /></svg>
);

// RV-4 — added to an iPhone's home screen, a review link opens full screen as "Reviews". The app
// page has no web manifest, so the phone keeps the exact address that was added (the link's code).
export function useHomeScreen(title = "Reviews") {
  useEffect(() => {
    const set = (sel: string, make: () => HTMLElement, attrs: Record<string, string>) => {
      let el = document.head.querySelector(sel) as HTMLElement | null;
      if (!el) { el = make(); document.head.appendChild(el); }
      for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    };
    const meta = () => document.createElement("meta"), link = () => document.createElement("link");
    set('meta[name="apple-mobile-web-app-capable"]', meta, { name: "apple-mobile-web-app-capable", content: "yes" });
    set('meta[name="mobile-web-app-capable"]', meta, { name: "mobile-web-app-capable", content: "yes" });
    set('meta[name="apple-mobile-web-app-status-bar-style"]', meta, { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" });
    set('meta[name="apple-mobile-web-app-title"]', meta, { name: "apple-mobile-web-app-title", content: title });
    set('link[rel="apple-touch-icon"]', link, { rel: "apple-touch-icon", href: "/reviews-icon-180.png" });
    set('meta[name="viewport"]', meta, { name: "viewport", content: "width=device-width, initial-scale=1.0, viewport-fit=cover" });
    const was = document.title; document.title = title;
    return () => { document.title = was; };
  }, [title]);
}
